import { useUser } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { useApi } from "../api-context";
import { clerkProfileSyncPayload } from "../clerk-profile-sync";

/** Keep the person profile current even on routes without workspace navigation. */
export function ClerkProfileSync() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const syncedProfileKey = useRef<string | null>(null);
  const currentPrincipal = useQuery({
    queryKey: ["current-principal"],
    queryFn: () => api.currentPrincipal(),
    enabled: user != null,
  });
  const updateProfile = useMutation({
    mutationFn: (input: { display_name?: string; primary_email?: string }) =>
      api.updateCurrentPrincipal(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["current-principal"] });
      await queryClient.invalidateQueries({ queryKey: ["principals"] });
      await queryClient.invalidateQueries({ queryKey: ["connections"] });
      await queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
  useEffect(() => {
    if (user == null || currentPrincipal.data === undefined) return;
    const payload = clerkProfileSyncPayload({
      storedDisplayName: currentPrincipal.data.display_name,
      storedEmail: currentPrincipal.data.primary_email,
      clerk: {
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        fullName: user.fullName ?? null,
        username: user.username ?? null,
        email: user.primaryEmailAddress?.emailAddress ?? null,
      },
    });
    if (payload === null) return;
    const key = JSON.stringify([
      user.id,
      currentPrincipal.data.principal_id,
      payload,
    ]);
    if (syncedProfileKey.current === key) return;
    syncedProfileKey.current = key;
    updateProfile.mutate(payload);
  }, [currentPrincipal.data, updateProfile, user]);
  return null;
}
